/* AgentMeeting — Agenda editor (wired to backend) */
import React from 'react';
import { AGENTS, AgendaT, STANCE_COLOR } from '../shared/data';
import {
  Avatar, StanceTag, ModelChip, Toggle, SegRadio, I,
} from '../shared/ui';
import { meetings as meetingsApi, agents as agentsApi, HTTPError, type AgentRecord, type ParticipantRecord } from '../shared/api';
import { useAuth } from '../shared/auth';
import { useI18n } from '../shared/i18n';

type Props = { embedded?: boolean; topBar?: React.ReactNode };

const MEETING_ID_KEY = 'agentmeeting.currentMeetingId';

const DEFAULT_TERMINATION_LABELS = [
  '所有轮次完成',
  '决策阈值达成 (≥ 4 / 6 同向)',
  '主持人手动定锤',
  '预算耗尽',
  '连续 3 轮无新论点 (rolling summary 检测)',
];

const DEFAULT_TERMINATION_KEYS = [
  'all_rounds_done',
  'decision_threshold',
  'host_manual',
  'budget_exhausted',
  'no_new_argument_3_rounds',
];

interface EditableRound {
  round: number;
  label: string;
  rule: string;
  instruction?: string;
  status?: 'done' | 'cur' | 'next';
}

interface MeetingShape {
  id: string;
  title: string;
  topic: string;
  type: string;
  status: string;
  budget_cents: number;
  config: any;
}

function navigate(route: string) {
  location.hash = '#/' + route;
  window.scrollTo(0, 0);
}

function fmtAgo(deltaMs: number): string {
  const s = Math.max(1, Math.round(deltaMs / 1000));
  if (s < 60) return `saved ${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `saved ${m}m ago`;
  const h = Math.round(m / 60);
  return `saved ${h}h ago`;
}

export function AgendaEditor({ embedded, topBar }: Props) {
  // useAuth ensures we have a user context; underlying token is used by api.ts
  useAuth();
  const { t } = useI18n();

  const [meetingId, setMeetingId] = React.useState<string | null>(() => {
    try { return localStorage.getItem(MEETING_ID_KEY); } catch { return null; }
  });

  const [loading, setLoading] = React.useState<boolean>(!!meetingId);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [title, setTitle] = React.useState<string>('');
  const [topic, setTopic] = React.useState<string>('');
  const [meetingStatus, setMeetingStatus] = React.useState<string>('draft');
  const [meetingType, setMeetingType] = React.useState<string>('decision');
  const [budgetCents, setBudgetCents] = React.useState<number>(500);
  const [windowSize, setWindowSize] = React.useState<number>(20);
  const [summaryInterval, setSummaryInterval] = React.useState<number>(5);
  const [rounds, setRounds] = React.useState<EditableRound[]>([]);
  const [termination, setTermination] = React.useState<string[]>([
    'all_rounds_done', 'decision_threshold', 'budget_exhausted',
  ]);

  const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = React.useState<number | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [, forceTick] = React.useState(0);

  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState<string | null>(null);

  // Real participants loaded from backend
  const [participants, setParticipants] = React.useState<ParticipantRecord[]>([]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [myAgents, setMyAgents] = React.useState<AgentRecord[]>([]);
  const [addingAgent, setAddingAgent] = React.useState<string | null>(null);
  const [removingPid, setRemovingPid] = React.useState<string | null>(null);

  const refreshParticipants = React.useCallback(async (mid: string) => {
    try {
      const res = await meetingsApi.listParticipants(mid);
      setParticipants(res.participants || []);
    } catch { /* non-fatal */ }
  }, []);

  const openPicker = React.useCallback(async () => {
    setPickerOpen(true);
    try {
      const res = await agentsApi.list();
      setMyAgents(res.agents || []);
    } catch { /* noop */ }
  }, []);

  const onAddAgent = async (agentId: string) => {
    if (!meetingId) return;
    setAddingAgent(agentId);
    try {
      const nextSeat = participants.length;
      await meetingsApi.addParticipant(meetingId, { kind: 'agent', ref_id: agentId, seat_no: nextSeat });
      await refreshParticipants(meetingId);
    } catch { /* noop */ } finally {
      setAddingAgent(null);
    }
  };

  const onRemoveParticipant = async (pid: string) => {
    if (!meetingId) return;
    setRemovingPid(pid);
    try {
      await meetingsApi.removeParticipant(meetingId, pid);
      setParticipants(prev => prev.filter(p => p.id !== pid));
    } catch { /* noop */ } finally {
      setRemovingPid(null);
    }
  };

  // Tick forward every 5s so "saved Ns ago" updates without input
  React.useEffect(() => {
    const t = setInterval(() => forceTick(x => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // Load meeting on mount / id change
  React.useEffect(() => {
    if (!meetingId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    meetingsApi.get(meetingId).then(m => {
      if (cancelled) return;
      const meeting = m as unknown as MeetingShape;
      setTitle(meeting.title || '');
      setTopic(meeting.topic || '');
      setMeetingStatus(meeting.status || 'draft');
      setMeetingType(meeting.type || 'decision');
      setBudgetCents(typeof meeting.budget_cents === 'number' ? meeting.budget_cents : 500);

      const cfg = meeting.config || {};
      const ws = Number(cfg.window_size);
      if (!Number.isNaN(ws) && ws > 0) setWindowSize(ws);
      const si = Number(cfg.summary_interval);
      if (!Number.isNaN(si) && si > 0) setSummaryInterval(si);

      const cfgRounds: any[] = Array.isArray(cfg.rounds) ? cfg.rounds : [];
      if (cfgRounds.length > 0) {
        setRounds(cfgRounds.map((r, i): EditableRound => ({
          round: typeof r.round === 'number' ? r.round : i,
          label: String(r.label ?? `Round ${i + 1}`),
          rule: String(r.rule ?? 'round-robin'),
          instruction: r.instruction != null ? String(r.instruction) : '',
          status: r.status,
        })));
      } else {
        setRounds([
          { round: 0, label: '开场陈述',   rule: 'round-robin', instruction: '请在 80 字内陈述初始立场, 给出 1 个核心论据。' },
          { round: 1, label: '反方质询',   rule: 'stance-pair', instruction: '反方先发言, 攻击 r1 的最薄弱论点, 给出具体数字反驳。' },
          { round: 2, label: '修正与回应', rule: 'reactive',    instruction: '回应 @你被提及的内容, 引用上一轮原话。' },
        ]);
      }

      if (Array.isArray(cfg.termination)) {
        setTermination(cfg.termination.map(String));
      }

      setLoading(false);
      hasUserEditedRef.current = false;
      // Load real participants (fire-and-forget; non-blocking)
      refreshParticipants(meetingId!);
    }).catch(err => {
      if (cancelled) return;
      const msg = err instanceof HTTPError
        ? `failed to load meeting (HTTP ${err.status})`
        : 'failed to load meeting';
      setLoadError(msg);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [meetingId]);

  // Debounced save
  const hasUserEditedRef = React.useRef(false);
  const saveTimerRef = React.useRef<number | null>(null);
  const inflightRef = React.useRef(false);
  const pendingRef = React.useRef(false);

  const doSave = React.useCallback(async () => {
    if (!meetingId) return;
    if (inflightRef.current) {
      pendingRef.current = true;
      return;
    }
    inflightRef.current = true;
    setSaveState('saving');
    setSaveError(null);
    const payload = {
      rounds: rounds.map(r => ({
        round: r.round,
        label: r.label,
        rule: r.rule,
        instruction: r.instruction || '',
      })),
      window_size: windowSize,
      summary_interval: summaryInterval,
      termination,
    };
    // eslint-disable-next-line no-console
    console.log('[agenda] saveAgenda', meetingId, payload);
    try {
      await meetingsApi.saveAgenda(meetingId, payload);
      setSaveState('saved');
      setLastSavedAt(Date.now());
    } catch (err) {
      setSaveState('error');
      setSaveError(err instanceof HTTPError ? `save failed (HTTP ${err.status})` : 'save failed');
    } finally {
      inflightRef.current = false;
      if (pendingRef.current) {
        pendingRef.current = false;
        // schedule another save with a tiny delay so React state has flushed
        window.setTimeout(() => { doSave(); }, 0);
      }
    }
  }, [meetingId, rounds, windowSize, summaryInterval, termination]);

  React.useEffect(() => {
    if (!meetingId) return;
    if (loading) return;
    if (!hasUserEditedRef.current) return;
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      doSave();
    }, 800);
    return () => {
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [meetingId, loading, rounds, windowSize, summaryInterval, termination, doSave]);

  // Helper that mutates state and marks edited
  function edit<T>(setter: React.Dispatch<React.SetStateAction<T>>) {
    return (v: React.SetStateAction<T>) => {
      hasUserEditedRef.current = true;
      setter(v);
    };
  }

  const editRounds          = edit(setRounds);
  const editWindow          = edit(setWindowSize);
  const editSummary         = edit(setSummaryInterval);
  const editTermination     = edit(setTermination);

  function updateRound(idx: number, patch: Partial<EditableRound>) {
    editRounds(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  }
  function addRound() {
    editRounds(prev => {
      const nextRound = prev.length;
      return [...prev, {
        round: nextRound,
        label: `Round ${nextRound + 1}`,
        rule: 'round-robin',
        instruction: '',
      }];
    });
  }
  function toggleTerm(key: string) {
    editTermination(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  async function onStart() {
    if (!meetingId) return;
    setStarting(true);
    setStartError(null);
    try {
      // Flush any pending save first
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        await doSave();
      }
      await meetingsApi.start(meetingId);
      navigate('app/room');
    } catch (err) {
      setStartError(err instanceof HTTPError ? `start failed (HTTP ${err.status})` : 'start failed');
    } finally {
      setStarting(false);
    }
  }

  // ─── Empty state ─────────────────────────────────────────
  if (!meetingId) {
    return (
      <div className="frame" style={{ height: '100%' }}>
        {topBar}
        <div className="grow flex center" style={{
          alignItems: 'center', justifyContent: 'center', minHeight: 0,
        }}>
          <div className="col gap-3" style={{
            padding: 28, border: '1px solid var(--line-2)', borderRadius: 6,
            background: 'var(--bg-1)', minWidth: 320, textAlign: 'center',
          }}>
            <div className="mono sm dim">No meeting selected</div>
            <div>
              <button className="btn primary" onClick={() => navigate('app/meetings')}>
                Open meetings →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading state ───────────────────────────────────────
  if (loading) {
    return (
      <div className="frame" style={{ height: '100%' }}>
        {topBar}
        <div className="grow flex center" style={{
          alignItems: 'center', justifyContent: 'center', minHeight: 0,
        }}>
          <div className="mono sm dim">loading meeting…</div>
        </div>
      </div>
    );
  }

  // ─── Load error ──────────────────────────────────────────
  if (loadError) {
    return (
      <div className="frame" style={{ height: '100%' }}>
        {topBar}
        <div className="grow flex center" style={{
          alignItems: 'center', justifyContent: 'center', minHeight: 0,
        }}>
          <div className="col gap-3" style={{
            padding: 24, border: '1px solid var(--line-2)', borderRadius: 6,
            background: 'var(--bg-1)', minWidth: 320, textAlign: 'center',
          }}>
            <div className="mono sm" style={{ color: 'var(--err)' }}>{loadError}</div>
            <div className="flex gap-2 center" style={{ justifyContent: 'center' }}>
              <button className="btn" onClick={() => {
                try { localStorage.removeItem(MEETING_ID_KEY); } catch { /* noop */ }
                setMeetingId(null);
              }}>clear selection</button>
              <button className="btn primary" onClick={() => navigate('app/meetings')}>
                Open meetings →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Save indicator ──────────────────────────────────────
  let savedLabel = 'draft';
  if (saveState === 'saving') savedLabel = 'saving…';
  else if (saveState === 'error') savedLabel = saveError || 'save failed';
  else if (saveState === 'saved' && lastSavedAt != null) {
    savedLabel = fmtAgo(Date.now() - lastSavedAt);
  } else if (lastSavedAt != null) {
    savedLabel = fmtAgo(Date.now() - lastSavedAt);
  }

  const totalRounds = rounds.length;
  const budgetUsd = (budgetCents / 100).toFixed(2);

  return (
    <div className="frame" style={{ height: '100%' }}>
      {topBar}
      {!embedded && (
        <div className="term-bar">
          <div className="lights"><span /><span /><span /></div>
          <div className="crumbs">
            <span>~/meetings/{title || meetingId.slice(0, 8)}</span>
            <span className="sep">/</span>
            <span className="here">agenda</span>
          </div>
          <div className="right">
            <span className="mono tiny dim" style={{
              color: saveState === 'error' ? 'var(--err)' : undefined,
            }}>{savedLabel}</span>
            <button className="btn" disabled>duplicate</button>
            {meetingStatus === 'running' || meetingStatus === 'paused' ? (
              <button className="btn primary" onClick={() => navigate('app/room')}>
                {I.play} 打开会议室 →
              </button>
            ) : meetingStatus === 'ended' ? (
              <button className="btn" onClick={() => navigate('app/replay')}>
                {I.rewind} 查看回放 →
              </button>
            ) : (
              <button className="btn primary" onClick={onStart} disabled={starting}>
                {I.play} {starting ? 'starting…' : t('app.agenda.start')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grow flex agenda-body" style={{ minHeight: 0 }}>
        <div className="col agenda-meta-col" style={{ width: 320, flex: '0 0 320px', borderRight: '1px solid var(--line-1)', background: 'var(--bg-1)' }}>
          <div className="col gap-3 scroll" style={{ padding: 18 }}>
            <div className="col gap-2">
              <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>MEETING</div>
              <input
                className="input"
                value={title}
                onChange={e => { hasUserEditedRef.current = true; setTitle(e.target.value); }}
              />
            </div>

            <div className="col gap-2">
              <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>TYPE</div>
              <SegRadio<'debate' | 'decision' | 'consult'>
                value={(meetingType === 'debate' || meetingType === 'consult') ? meetingType : 'decision'}
                options={[
                  { id: 'debate',   label: t('app.agenda.type.debate'),   color: 'var(--err)' },
                  { id: 'decision', label: t('app.agenda.type.decision'),   color: 'var(--live)' },
                  { id: 'consult',  label: '会诊',   color: 'var(--neutral)' },
                ]}
                onChange={(v) => { hasUserEditedRef.current = true; setMeetingType(v); }}
              />
            </div>

            <div className="col gap-2">
              <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.agenda.motion')}</div>
              <textarea
                className="input"
                rows={3}
                value={topic}
                onChange={e => { hasUserEditedRef.current = true; setTopic(e.target.value); }}
              />
            </div>

            <div className="hr" />

            <div className="col gap-2">
              <div className="flex between center">
                <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>
                  {t('app.agenda.participants')} · {participants.length}
                </div>
                <button className="btn ghost icon" onClick={openPicker} title="添加 agent 参会">
                  {I.plus}
                </button>
              </div>
              <div className="col gap-1">
                {participants.length === 0 && (
                  <div className="meta dim" style={{
                    padding: '10px 12px', border: '1px dashed var(--line-2)',
                    borderRadius: 4, textAlign: 'center', fontSize: 12,
                  }}>
                    还没有参与者 · 点 + 从你的 agent 库添加
                  </div>
                )}
                {participants.map((p, i) => (
                  <div key={p.id} className="flex center gap-2" style={{
                    padding: '6px 8px', borderRadius: 4, border: '1px solid var(--line-1)',
                  }}>
                    <span className="mono tiny dim" style={{ width: 18 }}>#{i + 1}</span>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: STANCE_COLOR[p.stance] || 'var(--pro)',
                      color: 'var(--bg-0)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700,
                      flexShrink: 0,
                    }}>{p.short || p.name.slice(0, 2).toUpperCase()}</div>
                    <div className="col grow" style={{ minWidth: 0 }}>
                      <div className="sm" style={{
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>{p.name}</div>
                      <div className="flex center gap-2">
                        <span className="mono tiny" style={{ color: STANCE_COLOR[p.stance] || 'var(--fg-3)' }}>
                          {p.stance}
                        </span>
                        <span className="mono tiny dim">{p.model}</span>
                      </div>
                    </div>
                    <button
                      className="btn ghost icon"
                      disabled={removingPid === p.id}
                      onClick={() => onRemoveParticipant(p.id)}
                      title="移除"
                      style={{ fontSize: 12, padding: '2px 6px', opacity: 0.5 }}
                    >✕</button>
                  </div>
                ))}
              </div>
            </div>

            {pickerOpen && (
              <AgentPickerModal
                myAgents={myAgents}
                presentIds={new Set(participants.map(p => p.ref_id))}
                addingAgent={addingAgent}
                onAdd={onAddAgent}
                onClose={() => setPickerOpen(false)}
              />
            )}

            <div className="hr" />

            <div className="col gap-2">
              <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.agenda.budget')}</div>
              <div className="flex center gap-2 mono sm">
                <span className="dim">cap</span>
                <input
                  value={budgetUsd}
                  onChange={e => {
                    const v = e.target.value.trim();
                    const n = Number(v);
                    if (!Number.isNaN(n) && n >= 0) {
                      hasUserEditedRef.current = true;
                      setBudgetCents(Math.round(n * 100));
                    }
                  }}
                  style={{
                    width: 70, padding: '6px 8px', background: 'var(--bg-2)',
                    border: '1px solid var(--line-2)', borderRadius: 4, color: 'var(--fg-0)',
                    fontFamily: 'var(--mono)',
                  }} />
                <span className="dim">usd · auto-pause on hit</span>
              </div>
              <div className="flex center gap-2 mono sm wrap">
                <span className="dim">window</span>
                <input
                  value={String(windowSize)}
                  onChange={e => {
                    const n = Number(e.target.value);
                    if (!Number.isNaN(n) && n > 0) editWindow(n);
                  }}
                  style={{
                    width: 50, padding: '6px 8px', background: 'var(--bg-2)',
                    border: '1px solid var(--line-2)', borderRadius: 4, color: 'var(--fg-0)',
                    fontFamily: 'var(--mono)',
                  }} />
                <span className="dim">last messages · rolling summary every</span>
                <input
                  value={String(summaryInterval)}
                  onChange={e => {
                    const n = Number(e.target.value);
                    if (!Number.isNaN(n) && n > 0) editSummary(n);
                  }}
                  style={{
                    width: 40, padding: '6px 8px', background: 'var(--bg-2)',
                    border: '1px solid var(--line-2)', borderRadius: 4, color: 'var(--fg-0)',
                    fontFamily: 'var(--mono)',
                  }} />
                <span className="dim">turns</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col grow scroll agenda-rounds-col" style={{ minWidth: 0, padding: '20px 28px 32px' }}>
          <div className="flex center between" style={{ marginBottom: 18 }}>
            <div className="col">
              <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>AGENDA · {totalRounds} rounds · est. ~{Math.max(1, totalRounds * 2)} min</div>
              <div style={{ fontSize: 18, fontWeight: 500, marginTop: 4 }}>议程编辑</div>
            </div>
            <div className="flex gap-2 center">
              <span className="mono tiny dim" style={{
                color: saveState === 'error' ? 'var(--err)' : undefined,
              }}>{savedLabel}</span>
              <button className="btn ghost">load template</button>
              <button className="btn" onClick={addRound}>{I.plus} {t('app.agenda.addRound')}</button>
              <button className="btn primary" onClick={onStart} disabled={starting}>
                {I.play} {starting ? 'starting…' : t('app.agenda.start')}
              </button>
            </div>
          </div>

          {startError && (
            <div className="mono tiny" style={{
              padding: '8px 12px', marginBottom: 12, border: '1px solid var(--err)',
              borderRadius: 4, color: 'var(--err)', background: 'var(--bg-1)',
            }}>{startError}</div>
          )}

          <div className="col gap-3">
            {rounds.map((r, i, arr) => (
              <RoundCard
                key={i}
                round={r}
                idx={i}
                isLast={i === arr.length - 1}
                onChange={(patch) => updateRound(i, patch)}
              />
            ))}
          </div>

          <div
            style={{ marginTop: 24, padding: 16, border: '1px dashed var(--line-2)', borderRadius: 6, textAlign: 'center', cursor: 'pointer' }}
            onClick={addRound}
          >
            <div className="mono sm dim">+ add round · or drag to reorder</div>
          </div>

          <div className="col gap-2" style={{ marginTop: 28 }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.agenda.termination')} · 任一满足即结束</div>
            <div className="col gap-1" style={{ border: '1px solid var(--line-1)', borderRadius: 4 }}>
              {DEFAULT_TERMINATION_LABELS.map((label, i) => {
                const key = DEFAULT_TERMINATION_KEYS[i];
                const on = termination.includes(key);
                const displayLabel = key === 'budget_exhausted'
                  ? `预算耗尽 ($${budgetUsd})`
                  : label;
                return (
                  <div key={key} className="flex center between" style={{
                    padding: '8px 12px', borderBottom: i < DEFAULT_TERMINATION_LABELS.length - 1 ? '1px solid var(--line-1)' : 'none',
                    cursor: 'pointer',
                  }} onClick={() => toggleTerm(key)}>
                    <span className="sm">{displayLabel}</span>
                    <Toggle on={on} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col agenda-timeline-col" style={{ width: 280, flex: '0 0 280px', borderLeft: '1px solid var(--line-1)', background: 'var(--bg-1)' }}>
          <div className="flex between center" style={{ padding: '12px 14px', borderBottom: '1px solid var(--line-1)' }}>
            <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.agenda.timeline')}</span>
          </div>
          <div className="col scroll" style={{ padding: 14, gap: 6 }}>
            <div className="meta" style={{ marginBottom: 10 }}>
              估算 1 名 speaker ≈ 35s · {AGENTS.filter(a => a.id !== 'U1').length} 名 speaker · {totalRounds} rounds
            </div>
            <TimelinePreview rounds={rounds} />
          </div>
        </div>
      </div>
    </div>
  );
}

function RoundCard({ round, idx, onChange }: {
  round: EditableRound;
  idx: number;
  isLast: boolean;
  onChange: (patch: Partial<EditableRound>) => void;
}) {
  const ruleOpts = [
    { id: 'round-robin', label: 'round-robin', desc: '按座次循环' },
    { id: 'stance-pair', label: 'stance-pair', desc: '正反交替' },
    { id: 'moderated',   label: 'moderated',   desc: '主持点名' },
    { id: 'free-form',   label: 'free-form',   desc: '抢麦 / claim-token' },
    { id: 'reactive',    label: 'reactive',    desc: '@提及优先' },
  ];
  const ruleId = (round.rule || '').split(' ·')[0].split(' ')[0] || 'round-robin';

  return (
    <div style={{ border: '1px solid var(--line-2)', borderRadius: 6, background: 'var(--bg-1)' }}>
      <div className="flex center" style={{ padding: '10px 14px', gap: 12, borderBottom: '1px solid var(--line-1)' }}>
        <span className="mono dim" style={{ width: 24 }}>⠿</span>
        <span className="mono sm" style={{
          padding: '2px 8px', background: 'var(--bg-3)', borderRadius: 3,
          color: round.status === 'cur' ? 'var(--live)' : 'var(--fg-1)',
        }}>r{(round.round ?? idx) + 1}</span>
        <input
          value={round.label}
          onChange={(e) => onChange({ label: e.target.value })}
          style={{
            flex: 1, background: 'transparent', border: 0, outline: 'none', color: 'var(--fg-0)',
            fontSize: 14, fontWeight: 500,
          }}
        />
        <span className="meta">~{[110, 160, 200, 140, 90, 120][idx % 6]}s</span>
        <button className="btn ghost icon">{I.dots}</button>
      </div>
      <div className="flex agenda-round-body" style={{ padding: 12, gap: 16 }}>
        <div className="col gap-2" style={{ flex: 2 }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>SCHEDULING RULE</div>
          <div className="flex gap-1 wrap">
            {ruleOpts.map(o => (
              <span
                key={o.id}
                className="tag"
                onClick={() => onChange({ rule: o.id })}
                style={{
                  cursor: 'pointer',
                  background: o.id === ruleId ? 'var(--bg-3)' : 'transparent',
                  color: o.id === ruleId ? 'var(--live)' : 'var(--fg-2)',
                  borderColor: o.id === ruleId ? 'var(--live)' : 'var(--line-2)',
                }}>{o.label}</span>
            ))}
          </div>
          <div className="meta">{ruleOpts.find(o => o.id === ruleId)?.desc}</div>
        </div>
        <div className="vr" />
        <div className="col gap-2" style={{ flex: 2 }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>INSTRUCTION · injected to speaker</div>
          <textarea
            className="input mono"
            rows={2}
            style={{ fontSize: 11 }}
            value={round.instruction || ''}
            onChange={(e) => onChange({ instruction: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

function TimelinePreview({ rounds }: { rounds: EditableRound[] }) {
  const speakers = AGENTS.filter(a => a.id !== 'U1');

  // Synthesize an AgendaT shape so visual layout matches old preview
  const visual: AgendaT[] = rounds.map((r, i) => ({
    round: r.round ?? i,
    label: r.label,
    rule: r.rule,
    status: (r.status as 'done' | 'cur' | 'next') || 'next',
  }));

  return (
    <div className="col gap-4">
      {visual.map((r, ri) => {
        const order = ri % 2 === 0 ? speakers : [...speakers].reverse();
        const cellCount = Math.min(speakers.length, ri === 4 ? 6 : 5);
        return (
          <div key={ri} className="col gap-1">
            <div className="mono tiny dim" style={{ letterSpacing: '0.06em' }}>
              R{(r.round ?? ri) + 1} · {r.label} · {(r.rule || '').split(' ·')[0]}
            </div>
            <div className="flex" style={{
              border: '1px solid var(--line-1)', borderRadius: 3, overflow: 'hidden', height: 22,
            }}>
              {order.slice(0, cellCount).map((a, i) => (
                <div key={i} title={a.name} style={{
                  flex: 1,
                  background: STANCE_COLOR[a.stance],
                  borderRight: i < cellCount - 1 ? '1px solid var(--bg-0)' : 'none',
                  fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600,
                  color: 'var(--bg-0)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  letterSpacing: '0.04em',
                }}>{a.short}</div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AgentPickerModal({ myAgents, presentIds, addingAgent, onAdd, onClose }: {
  myAgents: AgentRecord[];
  presentIds: Set<string>;
  addingAgent: string | null;
  onAdd: (id: string) => void;
  onClose: () => void;
}) {
  const available = myAgents.filter(a => !presentIds.has(a.id));
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 300,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '86vw', maxWidth: 540, maxHeight: '70vh',
          background: 'var(--bg-0)', border: '1px solid var(--line-2)',
          borderRadius: 8, display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div className="flex between center" style={{
          padding: '12px 16px', borderBottom: '1px solid var(--line-1)',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>添加参会 agent</div>
            <div className="meta dim" style={{ fontSize: 11 }}>
              从你的 agent 库选择 · 可多次添加
            </div>
          </div>
          <button className="btn ghost" onClick={onClose}>✕</button>
        </div>

        <div className="grow scroll" style={{ padding: '8px 12px' }}>
          {available.length === 0 ? (
            <div className="meta dim" style={{
              padding: 16, textAlign: 'center', fontSize: 12,
            }}>
              所有 agent 都已加入 · 或先去 Studio 创建新 agent
            </div>
          ) : (
            <div className="col gap-1">
              {available.map(a => (
                <div key={a.id} className="flex center gap-2" style={{
                  padding: '8px 10px', borderRadius: 4,
                  border: '1px solid var(--line-1)', cursor: 'pointer',
                }}
                  onClick={() => onAdd(a.id)}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%',
                    background: STANCE_COLOR[a.stance] || 'var(--pro)',
                    color: 'var(--bg-0)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontFamily: 'var(--mono)',
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                  }}>{a.short || a.name.slice(0, 2).toUpperCase()}</div>
                  <div className="col grow" style={{ minWidth: 0 }}>
                    <div className="sm" style={{
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{a.name}</div>
                    <div className="mono tiny dim">{a.provider} · {a.model}</div>
                  </div>
                  <button
                    className="btn primary"
                    disabled={addingAgent === a.id}
                    onClick={e => { e.stopPropagation(); onAdd(a.id); }}
                    style={{ fontSize: 11, padding: '4px 12px', flexShrink: 0 }}
                  >
                    {addingAgent === a.id ? '加入中…' : '+ 加入'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="meta dim" style={{
          padding: '8px 16px', borderTop: '1px solid var(--line-1)',
          fontSize: 11,
        }}>
          没有想要的角色? 去 Studio → 专家库 一键拉起
        </div>
      </div>
    </div>
  );
}
